import React, { useState } from 'react';
import {
  FiX,
  FiEdit3,
  FiCalendar,
  FiUser,
  FiFlag,
  FiClock,
  FiTag,
  FiMessageCircle,
  FiCheckCircle,
  FiCircle,
  FiPlay,
  FiUsers,
  FiTrash2,
  FiAlertTriangle,
  FiSend
} from 'react-icons/fi';
import axios from 'axios';
import TaskTimer from './TaskTimer';

const TaskDetailModal = ({ task, onClose, onUpdate, onDelete, canEdit, tags }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [currentStatus, setCurrentStatus] = useState(task.status);
  const [editData, setEditData] = useState({
    title: task.title,
    description: task.description || '',
    status: task.status,
    priority: task.priority,
    scheduled_date: task.scheduled_date || '',
    deadline_date: task.deadline_date || '',
    estimated_hours: task.estimated_hours || '',
    actual_hours: task.actual_hours || '',
    color: task.color || '#6b7280'
  });
  const [taskData, setTaskData] = useState(task);
  const [loading, setLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loadingComments, setLoadingComments] = useState(false);
  const [submittingComment, setSubmittingComment] = useState(false);

  React.useEffect(() => {
    if (task.id) {
      fetchComments();
    }
  }, [task.id]);

  const fetchComments = async () => {
    setLoadingComments(true);
    try {
      const response = await axios.get(`/api/planwise/tasks/${task.id}/comments`);
      setComments(response.data);
    } catch (error) {
      console.error('Error fetching comments:', error);
    } finally {
      setLoadingComments(false);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;

    setSubmittingComment(true);
    try {
      const response = await axios.post(`/api/planwise/tasks/${task.id}/comments`, {
        content: newComment.trim()
      });
      setComments([...comments, response.data]);
      setNewComment('');
    } catch (error) {
      console.error('Error adding comment:', error);
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      await axios.delete(`/api/planwise/comments/${commentId}`);
      setComments(comments.filter(c => c.id !== commentId));
    } catch (error) {
      console.error('Error deleting comment:', error);
    }
  };

  const statusConfig = {
    pending: { icon: FiCircle, color: 'text-gray-600 dark:text-gray-300', bg: 'bg-gray-100 dark:bg-gray-700', label: 'Pending' },
    in_progress: { icon: FiPlay, color: 'text-blue-600 dark:text-blue-300', bg: 'bg-blue-100 dark:bg-blue-900/30', label: 'In Progress' },
    completed: { icon: FiCheckCircle, color: 'text-green-600 dark:text-green-300', bg: 'bg-green-100 dark:bg-green-900/30', label: 'Completed' },
    delegated: { icon: FiUsers, color: 'text-purple-600 dark:text-purple-300', bg: 'bg-purple-100 dark:bg-purple-900/30', label: 'Delegated' }
  };

  const priorityConfig = {
    low: { color: 'text-gray-600 dark:text-gray-300', bg: 'bg-gray-100 dark:bg-gray-700' },
    medium: { color: 'text-yellow-700 dark:text-yellow-300', bg: 'bg-yellow-100 dark:bg-yellow-900/30' },
    high: { color: 'text-orange-700 dark:text-orange-300', bg: 'bg-orange-100 dark:bg-orange-900/30' },
    urgent: { color: 'text-red-700 dark:text-red-300', bg: 'bg-red-100 dark:bg-red-900/30' }
  };

  const handleSave = async () => {
    if (!editData.title.trim()) return;

    setLoading(true);
    try {
      const updates = {
        ...editData,
        estimated_hours: editData.estimated_hours ? parseInt(editData.estimated_hours) : null,
        actual_hours: editData.actual_hours ? parseInt(editData.actual_hours) : null,
        scheduled_date: editData.scheduled_date || null,
        deadline_date: editData.deadline_date || null
      };

      const response = await axios.put(`/api/planwise/tasks/${task.id}`, updates);

      setTaskData(response.data);
      onUpdate(task.id, response.data);
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating task:', error);
    } finally {
      setIsEditing(false);
    }
  };

  const handleDelete = async () => {
    if (!onDelete) return;

    setDeleting(true);
    try {
      await onDelete(task.id);
      onClose();
    } catch (error) {
      console.error('Error deleting task:', error);
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    if (!canEdit) return;

    try {
      await onUpdate(task.id, { status: newStatus });
      setCurrentStatus(newStatus);
    } catch (error) {
      console.error('Error updating task status:', error);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleDateString('en-GB', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const getTimeAgo = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));

    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d ago`;
    return date.toLocaleDateString();
  };

  const inputCls = "w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm text-gray-900 dark:text-white";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-2xl max-h-[90vh] bg-white dark:bg-gray-800 rounded-lg shadow-xl ring-1 ring-gray-200 dark:ring-gray-700 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-base font-semibold text-gray-900 dark:text-white truncate mr-3">
            {task.title}
          </h2>
          <div className="flex items-center gap-1 flex-shrink-0">
            {canEdit && (
              <>
                <button
                  onClick={() => setIsEditing(!isEditing)}
                  className="p-1.5 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
                  title={isEditing ? 'Cancel editing' : 'Edit task'}
                >
                  <FiEdit3 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="p-1.5 text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors"
                  title="Delete task"
                >
                  <FiTrash2 className="w-4 h-4" />
                </button>
              </>
            )}
            <button
              onClick={onClose}
              className="p-1.5 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
              aria-label="Close"
            >
              <FiX className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className={`p-4 overflow-y-auto ${isEditing ? 'max-h-[calc(90vh-160px)]' : 'max-h-[calc(90vh-110px)]'}`}>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Main */}
            <div className="lg:col-span-2 space-y-4">
              {/* Title + Description */}
              <div>
                {isEditing ? (
                  <input
                    type="text"
                    value={editData.title}
                    onChange={(e) => setEditData(prev => ({ ...prev, title: e.target.value }))}
                    className={`${inputCls} text-lg font-semibold mb-2`}
                    placeholder="Task title"
                  />
                ) : (
                  <h1 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    {task.title}
                  </h1>
                )}

                {isEditing ? (
                  <textarea
                    value={editData.description}
                    onChange={(e) => setEditData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Task description"
                    rows={3}
                    className={`${inputCls} resize-none`}
                  />
                ) : (
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {task.description || 'No description provided.'}
                  </p>
                )}
              </div>

              {/* Status */}
              <div>
                <h3 className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2 uppercase tracking-wide">Status</h3>
                <div className="flex flex-wrap gap-1.5">
                  {Object.entries(statusConfig).map(([status, config]) => (
                    <button
                      key={status}
                      onClick={() => handleStatusChange(status)}
                      disabled={!canEdit}
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border text-xs font-medium transition-colors ${
                        currentStatus === status
                          ? `${config.bg} border-current ${config.color}`
                          : 'bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:border-gray-300 dark:hover:border-gray-600'
                      } ${canEdit ? 'cursor-pointer' : 'cursor-default'}`}
                    >
                      <config.icon className="w-3 h-3" />
                      {config.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Tags */}
              {task.tags && task.tags.length > 0 && (
                <div>
                  <h3 className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2 uppercase tracking-wide">Tags</h3>
                  <div className="flex flex-wrap gap-1.5">
                    {task.tags.map(tag => (
                      <span
                        key={tag.id}
                        className="px-2 py-0.5 text-xs rounded-full border"
                        style={{
                          backgroundColor: `${tag.color}20`,
                          color: tag.color,
                          borderColor: `${tag.color}40`
                        }}
                      >
                        {tag.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Comments */}
              <div>
                <h3 className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2 uppercase tracking-wide flex items-center gap-1.5">
                  <FiMessageCircle className="w-3.5 h-3.5" />
                  Comments ({comments.length})
                </h3>

                <div className="space-y-2 mb-3 max-h-60 overflow-y-auto">
                  {loadingComments ? (
                    <div className="text-center py-3 text-xs text-gray-400">Loading comments…</div>
                  ) : comments.length > 0 ? (
                    comments.map(comment => (
                      <div key={comment.id} className={`rounded-md p-2.5 ${
                        comment.is_system
                          ? 'bg-blue-50 dark:bg-blue-900/20 border-l-2 border-blue-400'
                          : 'bg-gray-50 dark:bg-gray-700/50'
                      }`}>
                        <div className="flex items-start justify-between mb-1">
                          <div className="flex items-center gap-2 min-w-0">
                            <div className="w-5 h-5 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center flex-shrink-0">
                              {comment.is_system ? (
                                <FiClock className="w-2.5 h-2.5 text-blue-600 dark:text-blue-400" />
                              ) : (
                                <span className="text-[10px] font-medium text-blue-600 dark:text-blue-400">
                                  {comment.author?.firstName?.charAt(0) || 'U'}
                                </span>
                              )}
                            </div>
                            <span className="text-xs font-medium text-gray-900 dark:text-white truncate">
                              {comment.is_system
                                ? `SYSTEM — ${comment.author?.firstName} ${comment.author?.lastName}`
                                : `${comment.author?.firstName} ${comment.author?.lastName}`
                              }
                            </span>
                            <span className="text-[10px] text-gray-500 dark:text-gray-400 flex-shrink-0">
                              {new Date(comment.created_at).toLocaleDateString('en-GB', {
                                day: 'numeric',
                                month: 'short',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </span>
                          </div>

                          {canEdit && !comment.is_system && (
                            <button
                              onClick={() => handleDeleteComment(comment.id)}
                              className="p-1 text-gray-400 hover:text-red-500 rounded transition-colors flex-shrink-0"
                              title="Delete comment"
                            >
                              <FiTrash2 className="w-3 h-3" />
                            </button>
                          )}
                        </div>

                        <p className={`text-xs leading-relaxed ${
                          comment.is_system
                            ? 'text-blue-700 dark:text-blue-300 font-medium'
                            : 'text-gray-700 dark:text-gray-300'
                        }`}>
                          {comment.content}
                        </p>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-3 text-xs text-gray-400">
                      <FiMessageCircle className="w-5 h-5 mx-auto mb-1" />
                      No comments yet
                    </div>
                  )}
                </div>

                {canEdit && (
                  <div className="flex gap-2">
                    <textarea
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      placeholder="Add a comment…"
                      className={`${inputCls} resize-none`}
                      rows={2}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                          handleAddComment();
                        }
                      }}
                    />
                    <button
                      onClick={handleAddComment}
                      disabled={!newComment.trim() || submittingComment}
                      className="px-3 py-2 bg-primary-600 hover:bg-primary-700 disabled:bg-gray-400 text-white rounded-md transition-colors flex items-center justify-center flex-shrink-0"
                      title="Send comment (Ctrl+Enter)"
                    >
                      {submittingComment ? (
                        <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      ) : (
                        <FiSend className="w-3.5 h-3.5" />
                      )}
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-3">
              <div className="bg-gray-50 dark:bg-gray-700/30 rounded-md p-3 space-y-3">
                {/* Priority */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    <FiFlag className="inline w-3 h-3 mr-1 -mt-0.5" />
                    Priority
                  </label>
                  {isEditing ? (
                    <select
                      value={editData.priority}
                      onChange={(e) => setEditData(prev => ({ ...prev, priority: e.target.value }))}
                      className={inputCls}
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="urgent">Urgent</option>
                    </select>
                  ) : (
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${priorityConfig[task.priority]?.bg} ${priorityConfig[task.priority]?.color}`}>
                      <FiFlag className="w-3 h-3" />
                      {task.priority.charAt(0).toUpperCase() + task.priority.slice(1)}
                    </span>
                  )}
                </div>

                {/* Assignee */}
                {task.assignee && (
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                      <FiUser className="inline w-3 h-3 mr-1 -mt-0.5" />
                      Assigned to
                    </label>
                    <div className="flex items-center gap-1.5">
                      <div className="w-5 h-5 bg-primary-500 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-white text-[9px] font-semibold">
                          {task.assignee.firstName?.charAt(0)}{task.assignee.lastName?.charAt(0)}
                        </span>
                      </div>
                      <span className="text-xs text-gray-900 dark:text-white truncate">
                        {task.assignee.firstName} {task.assignee.lastName}
                      </span>
                    </div>
                  </div>
                )}

                {/* Dates */}
                <div className="space-y-2">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                      <FiCalendar className="inline w-3 h-3 mr-1 -mt-0.5" />
                      Scheduled
                    </label>
                    {isEditing ? (
                      <input
                        type="date"
                        value={editData.scheduled_date}
                        onChange={(e) => setEditData(prev => ({ ...prev, scheduled_date: e.target.value }))}
                        className={inputCls}
                      />
                    ) : (
                      <p className="text-xs text-gray-600 dark:text-gray-400">
                        {formatDate(task.scheduled_date)}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                      <FiFlag className="inline w-3 h-3 mr-1 -mt-0.5" />
                      Deadline
                    </label>
                    {isEditing ? (
                      <input
                        type="date"
                        value={editData.deadline_date}
                        onChange={(e) => setEditData(prev => ({ ...prev, deadline_date: e.target.value }))}
                        className={inputCls}
                      />
                    ) : (
                      <p className="text-xs text-gray-600 dark:text-gray-400">
                        {formatDate(task.deadline_date)}
                      </p>
                    )}
                  </div>
                </div>

                {/* Time tracking */}
                <div className="space-y-2">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                      <FiClock className="inline w-3 h-3 mr-1 -mt-0.5" />
                      Estimated hours
                    </label>
                    {isEditing ? (
                      <input
                        type="number"
                        value={editData.estimated_hours}
                        onChange={(e) => setEditData(prev => ({ ...prev, estimated_hours: e.target.value }))}
                        placeholder="0"
                        min="0"
                        step="0.5"
                        className={inputCls}
                      />
                    ) : (
                      <p className="text-xs text-gray-600 dark:text-gray-400">
                        {task.estimated_hours ? `${task.estimated_hours}h` : 'Not set'}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Actual hours
                    </label>
                    {isEditing ? (
                      <input
                        type="number"
                        value={editData.actual_hours}
                        onChange={(e) => setEditData(prev => ({ ...prev, actual_hours: e.target.value }))}
                        placeholder="0"
                        min="0"
                        step="0.5"
                        className={inputCls}
                      />
                    ) : (
                      <div className="space-y-2">
                        <p className="text-xs text-gray-600 dark:text-gray-400">
                          {taskData.actual_hours ? `${Math.floor(taskData.actual_hours / 60)}h ${taskData.actual_hours % 60}m` : 'Not tracked'}
                        </p>
                        {currentStatus !== 'completed' && (
                          <TaskTimer
                            task={taskData}
                            onTimeUpdate={(taskId, newActualHours) => {
                              setTaskData(prev => ({ ...prev, actual_hours: newActualHours }));
                              setEditData(prev => ({ ...prev, actual_hours: newActualHours }));
                              if (onUpdate) {
                                onUpdate(taskId, { actual_hours: newActualHours });
                              }
                            }}
                            className="mt-1"
                          />
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Created info */}
                <div className="pt-2 border-t border-gray-200 dark:border-gray-600">
                  <div className="text-[11px] text-gray-500 dark:text-gray-400 space-y-0.5">
                    <p>Created {getTimeAgo(task.created_at)}</p>
                    {task.creator && (
                      <p>by {task.creator.firstName} {task.creator.lastName}</p>
                    )}
                    {task.updated_at !== task.created_at && (
                      <p>Updated {getTimeAgo(task.updated_at)}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        {isEditing && (
          <div className="flex gap-2 px-5 py-3 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={() => {
                setIsEditing(false);
                setEditData({
                  title: task.title,
                  description: task.description || '',
                  status: task.status,
                  priority: task.priority,
                  scheduled_date: task.scheduled_date || '',
                  deadline_date: task.deadline_date || '',
                  estimated_hours: task.estimated_hours || '',
                  actual_hours: task.actual_hours || '',
                  color: task.color || '#6b7280'
                });
              }}
              className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={loading || !editData.title.trim()}
              className="flex-1 px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:bg-gray-400 rounded-md transition-colors"
            >
              {loading ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        )}

        {/* Delete Confirmation */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-gray-900/60 flex items-center justify-center z-[60] p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-5 max-w-sm w-full shadow-xl">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
                  <FiAlertTriangle className="w-4 h-4 text-red-600 dark:text-red-400" />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                    Delete Task
                  </h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    This action cannot be undone
                  </p>
                </div>
              </div>

              <p className="text-sm text-gray-700 dark:text-gray-300 mb-4">
                Are you sure you want to delete "<strong>{task.title}</strong>"? This will also remove any dependencies and comments associated with this task.
              </p>

              <div className="flex gap-2">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  disabled={deleting}
                  className="flex-1 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  disabled={deleting}
                  className="flex-1 px-3 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md transition-colors disabled:opacity-50"
                >
                  {deleting ? 'Deleting…' : 'Delete Task'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TaskDetailModal;
